import { useState } from "react";
import { Modal, Button, Form, Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from "axios";
import { AiOutlineEdit, AiOutlineDelete, AiOutlineUserAdd, AiOutlineSafetyCertificate, AiOutlineCheckCircle, AiOutlinePlus, AiOutlineMinus, AiOutlineCreditCard, AiOutlineLock, AiOutlineClockCircle, AiOutlineInfoCircle, AiOutlineSetting, AiOutlineBank, AiOutlineTeam, AiOutlineFileText, AiOutlineCar } from "react-icons/ai";
import { useTranslation, Trans } from 'react-i18next';


const roleDisplayConfig = {
  user: { Icon: AiOutlineCheckCircle, labelKey: "user" },
  admin: { Icon: AiOutlineSafetyCertificate, labelKey: "admin" },
  new: { Icon: AiOutlineUserAdd, labelKey: "new" },
};

const UsersTab = ({ users, fetchUsers, roleFilter, setRoleFilter }) => {
  const { t } = useTranslation();

  const [editModal, setEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [userData, setUserData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    role: "user",
    index: "",
    street: "",
    house_number: "",
    city: "",
    pesel: "",
    account_number: "",
    account_salary: "",
    name_transfers: "",
    name_salary: ""
  });
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [editingRoleUserId, setEditingRoleUserId] = useState(null);
  const [pendingAdminRole, setPendingAdminRole] = useState(null);
  const [driverCardForm, setDriverCardForm] = useState({ name: "", cardNumber: "" });
  const [driverCardFormError, setDriverCardFormError] = useState("");
  const [driverIdentifiers, setDriverIdentifiers] = useState([]);
  const [showAddDriverIdentifier, setShowAddDriverIdentifier] = useState(false);
  const [editingIdentifierId, setEditingIdentifierId] = useState(null);
  const [editingIdentifierForm, setEditingIdentifierForm] = useState({ name: "", cardNumber: "" });
  const [editingIdentifierError, setEditingIdentifierError] = useState("");
  const [driverDefaultValues, setDriverDefaultValues] = useState(null);
  const [driverDefaultValuesLoading, setDriverDefaultValuesLoading] = useState(false);
  const [driverVehicle, setDriverVehicle] = useState(null);
  const [driverVehicleLoading, setDriverVehicleLoading] = useState(false);


  const sortedUsers = [...users].sort((a, b) => {
    if (!sortConfig.key) return 0;
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const validateFields = () => {
    let newErrors = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email.trim())) {
      newErrors.email = t("emailWarning");
    }
    if (!/^[A-Za-z]+$/.test(userData.firstName.trim())) {
      newErrors.firstName = t("nameWarning");
    }
    if (!/^[A-Za-z]+$/.test(userData.lastName.trim())) {
      newErrors.lastName = t("surnameWarning");
    }
    if (!/^\+48\d{9}$/.test(userData.phone)) {
      newErrors.phone = t("phoneWarning");
    }
    if (userData.pesel && !/^\d{11}$/.test(userData.pesel)) {
      newErrors.pesel = t("peselWarning");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEditClick = async (user) => {
    setSelectedUser(user);
    setUserData({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone,
      role: user.role || "user",
      index: user.index || "",
      street: user.street || "",
      house_number: user.house_number || "",
      city: user.city || "",
      pesel: user.pesel || "",
      account_number: user.account_number || "",
      account_salary: user.account_salary || "",
      name_transfers: user.name_transfers || "",
      name_salary: user.name_salary || ""
    });
    setErrors({});
    setDriverCardForm({ name: "", cardNumber: "" });
    setDriverCardFormError("");
    setShowAddDriverIdentifier(false);
    setEditingIdentifierId(null);
    setEditingIdentifierError("");
    try {
      const { data } = await axios.get(`/api/users/${user.id}/identifiers`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const list = Array.isArray(data) ? data : [];
      setDriverIdentifiers(
        list.map((row) => ({
          id: row.id,
          name: row.name || "",
          cardNumber: row.cardNumber ?? row.card_number ?? "",
        }))
      );
    } catch (err) {
      setDriverIdentifiers([]);
      const msg = err.response?.data?.error || err.message;
      console.error("GET identifiers failed:", msg);
      alert(t("errorLoadingIdentifiers") || `Не вдалося завантажити карти: ${msg}`);
    }
    setDriverDefaultValuesLoading(true);
    setDriverDefaultValues(null);
    try {
      const { data } = await axios.get(`/api/extra-funds-default-values/by-driver/${user.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setDriverDefaultValues(data && typeof data === "object" ? data : null);
    } catch {
      setDriverDefaultValues(null);
    } finally {
      setDriverDefaultValuesLoading(false);
    }
    setDriverVehicleLoading(true);
    setDriverVehicle(null);
    try {
      const { data: vehicles } = await axios.get("/api/fleet/vehicles", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const list = Array.isArray(vehicles) ? vehicles : [];
      const assigned = list.find((v) => v.driver_id === user.id) || null;
      setDriverVehicle(assigned);
    } catch {
      setDriverVehicle(null);
    } finally {
      setDriverVehicleLoading(false);
    }
    setEditModal(true);
  };

  const isCardNumberValid = (value) => /^[A-Za-z0-9]+$/.test(String(value).trim());

  const saveIdentifiersToServer = async (list) => {
    if (!selectedUser?.id) return;
    const payload = list.map((item) => ({
      name: String(item.name || "").trim(),
      cardNumber: String(item.cardNumber ?? item.card_number ?? "").trim(),
    }));
    const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    try {
      await axios.put(`/api/users/${selectedUser.id}/identifiers`, { identifiers: payload }, { headers });
    } catch (error) {
      const msg = error.response?.data?.error || error.message;
      console.error("PUT identifiers failed:", msg);
      alert(t("errorSavingIdentifiers") || `Помилка збереження карток: ${msg}`);
    }
  };

  const handleAddDriverIdentifier = async () => {
    const trimmedName = driverCardForm.name.trim();
    const trimmedNumber = driverCardForm.cardNumber.trim();
    setDriverCardFormError("");
    if (!trimmedName || !trimmedNumber) return;
    if (!isCardNumberValid(trimmedNumber)) {
      setDriverCardFormError(t("driverCardNumberInvalid"));
      return;
    }
    const newItem = { id: Date.now(), name: trimmedName, cardNumber: trimmedNumber };
    const newList = [...driverIdentifiers, newItem];
    setDriverIdentifiers(newList);
    setDriverCardForm({ name: "", cardNumber: "" });
    setShowAddDriverIdentifier(false);
    await saveIdentifiersToServer(newList);
  };

  const handleEditDriverIdentifier = (item) => {
    setEditingIdentifierId(item.id);
    setEditingIdentifierForm({ name: item.name, cardNumber: item.cardNumber });
    setEditingIdentifierError("");
  };

  const handleSaveEditDriverIdentifier = async () => {
    const name = editingIdentifierForm.name.trim();
    const cardNumber = editingIdentifierForm.cardNumber.trim();
    setEditingIdentifierError("");
    if (!name || !cardNumber) return;
    if (!isCardNumberValid(cardNumber)) {
      setEditingIdentifierError(t("driverCardNumberInvalid"));
      return;
    }
    const newList = driverIdentifiers.map((item) =>
      item.id === editingIdentifierId ? { ...item, name, cardNumber } : item
    );
    setDriverIdentifiers(newList);
    setEditingIdentifierId(null);
    await saveIdentifiersToServer(newList);
  };

  const handleCancelEditDriverIdentifier = () => {
    setEditingIdentifierId(null);
    setEditingIdentifierError("");
  };

  const handleRemoveDriverIdentifier = async (id) => {
    const newList = driverIdentifiers.filter((item) => item.id !== id);
    setDriverIdentifiers(newList);
    if (editingIdentifierId === id) setEditingIdentifierId(null);
    await saveIdentifiersToServer(newList);
  };

  const handleUpdate = async () => {
    if (!validateFields()) {
      alert(t("errorsWarning"));
      return;
    }
    const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    try {
      await axios.put(`/api/userUpdate/${selectedUser.id}`, userData, { headers });
      fetchUsers();
      setEditModal(false);
    } catch (error) {
      const msg = error.response?.data?.error || error.message;
      console.error("PUT userUpdate failed:", msg);
      alert(t("errorUpdatingUser") || `Помилка оновлення користувача: ${msg}`);
    }
  };

  const handleResetPassword = async (userId) => {
    const generateRandomPassword = () => {
      const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()";
      let password = "";
      for (let i = 0; i < 10; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return password;
    };

    const newPassword = generateRandomPassword(); // Генеруємо випадковий пароль
    const user = users.find(u => u.id === userId);
    if (!user) {
      alert("Користувача не знайдено.");
      return;
    }
    if (!window.confirm(`Новий пароль для ${user.email}: ${newPassword}\nСкинути пароль?`)) {
      return;
    }

    try {
      const response = await axios.put(`/api/userResetPassword/${userId}`, {
        password: newPassword,
      });

      const loginLink = "https://godrive.com.pl/login";
      const fence = "```";
      const message =
        `🔐 ${t("loginCredentials")}:\n` +
        `Email:\n${fence}\n${user.email}\n${fence}\n` +
        `${t("password")}:\n${fence}\n${newPassword}\n${fence}\n` +
        `${t("loginLink")} ${loginLink}`;

      // Копіюємо в буфер обміну
      await navigator.clipboard.writeText(message);

      alert(`Пароль успішно скинуто! Новий пароль: ${newPassword}`);
    } catch (error) {
      console.error("Помилка скидання пароля:", error);
      alert("Не вдалося скинути пароль. Спробуйте ще раз.");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Ви впевнені, що хочете видалити цього користувача? Цю дію не можна скасувати!")) {
      return;
    }

    try {
      await axios.delete(`/api/deleteUser/${userId}`);
      fetchUsers();
    } catch (error) {
      console.error("Помилка видалення користувача:", error);
      alert("Не вдалося видалити користувача. Спробуйте ще раз.");
    }
  };

  const handleRoleChange = async (user, newRole) => {
    if (newRole === user.role) {
      setEditingRoleUserId(null);
      return;
    }
    if (newRole === "admin") {
      setEditingRoleUserId(null);
      setPendingAdminRole({ user, newRole });
      return;
    }
    await applyRoleChange(user, newRole);
  };

  const applyRoleChange = async (user, newRole) => {
    try {
      const payload = {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone,
        role: newRole,
        index: user.index || "",
        street: user.street || "",
        house_number: user.house_number || "",
        city: user.city || "",
        pesel: user.pesel || "",
        account_number: user.account_number || "",
        account_salary: user.account_salary || "",
        name_transfers: user.name_transfers || "",
        name_salary: user.name_salary || "",
      };
      await axios.put(`/api/userUpdate/${user.id}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      fetchUsers();
      setEditingRoleUserId(null);
      setPendingAdminRole(null);
    } catch (error) {
      console.error("Помилка оновлення ролі:", error);
      alert(t("errorUpdatingRole"));
    }
  };

  const handleConfirmAdminRole = async () => {
    if (pendingAdminRole) {
      await applyRoleChange(pendingAdminRole.user, pendingAdminRole.newRole);
    }
  };



  return (
    <div>
      <h4>{t("usersList")}</h4>
      <div className="d-flex align-items-center gap-2 mb-3">
        <Form.Control
          type="text"
          placeholder={t("searchByNameOrSurname")}
          className="flex-grow-1"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <a
          href="/admin/addnewuser"
          className="border rounded px-3 py-2 d-flex align-items-center gap-2 text-decoration-none text-white flex-shrink-0"
          style={{
            cursor: "pointer",
            backgroundColor: "var(--bs-success)",
            borderColor: "var(--bs-success)",
          }}
        >
          <AiOutlineUserAdd size={20} />
          <span className="fw-semibold">{t("addUser")}</span>
        </a>
      </div>
      
      <table className="table table-striped mt-3">
        <thead>
          <tr>
            <th>ID</th>
            <th>{t("email")}</th>
            <th onClick={() => handleSort("firstName")} style={{ cursor: "pointer" }}>
              {t("name")} {sortConfig.key === "firstName" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
            </th>
            <th onClick={() => handleSort("lastName")} style={{ cursor: "pointer" }}>
              {t("surname")} {sortConfig.key === "lastName" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
            </th>
            <th>{t("phone")}</th>
            <th onClick={() => handleSort("role")} style={{ cursor: "pointer" }}>
              {t("role")} {sortConfig.key === "role" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
            </th>
            <th>{t("actions")}</th>
          </tr>
        </thead>
        <tbody>
          {sortedUsers
            .filter((user) => {
              const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
              const matchesSearch = fullName.includes(searchTerm.toLowerCase());
              const matchesRole = roleFilter === "all" || user.role === roleFilter;
              return matchesSearch && matchesRole;
            })
            .map((user) => (
              <tr
                key={user.id}
                className={user.role === "new" ? "table-warning" : ""}
              >
                <td>{user.id}</td>
                <td>{user.email}</td>
                <td>{user.firstName}</td>
                <td>{user.lastName}</td>
                <td>{user.phone}</td>
                <td
                  className={user.role === "admin" ? "table-danger" : ""}
                  onDoubleClick={() => setEditingRoleUserId(user.id)}
                  style={{ cursor: "pointer", minWidth: "100px" }}
                >
                  {editingRoleUserId === user.id ? (
                    <Form.Control
                      as="select"
                      size="sm"
                      value={user.role}
                      onChange={(e) => handleRoleChange(user, e.target.value)}
                      onBlur={() => setEditingRoleUserId(null)}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="user">{t("user")}</option>
                      <option value="admin">{t("admin")}</option>
                      <option value="new">{t("new")}</option>
                    </Form.Control>
                  ) : (
                    (() => {
                      const config = roleDisplayConfig[user.role] || roleDisplayConfig.user;
                      const Icon = config.Icon;
                      return (
                        <span className="d-flex align-items-center gap-2">
                          <Icon size={18} />
                          {t(config.labelKey)}
                        </span>
                      );
                    })()
                  )}
                </td>
                <td>
                  <AiOutlineEdit
                    style={{ cursor: "pointer", marginRight: "10px", color: "orange" }}
                    size={20}
                    onClick={() => handleEditClick(user)}
                  />
                  <AiOutlineDelete
                    style={{ cursor: "pointer", color: "red" }}
                    size={20}
                    onClick={() => handleDeleteUser(user.id)}
                  />
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {/* Модальне вікно редагування */}
      <Modal show={editModal} onHide={() => setEditModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("edit")} {t("user").toLocaleLowerCase()}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <Form>
                <Form.Group className="mb-2">
                  <Form.Label>{t("email")}</Form.Label>
                  <Form.Control type="email" value={userData.email} onChange={(e) => setUserData({ ...userData, email: e.target.value })} />
                  {errors.email && <div className="text-danger">{errors.email}</div>}
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>{t("name")}</Form.Label>
                  <Form.Control type="text" value={userData.firstName} onChange={(e) => setUserData({ ...userData, firstName: e.target.value })} />
                  {errors.firstName && <div className="text-danger">{errors.firstName}</div>}
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>{t("surname")}</Form.Label>
                  <Form.Control type="text" value={userData.lastName} onChange={(e) => setUserData({ ...userData, lastName: e.target.value })} />
                  {errors.lastName && <div className="text-danger">{errors.lastName}</div>}
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>{t("phone")}</Form.Label>
                  <Form.Control type="text" value={userData.phone} onChange={(e) => setUserData({ ...userData, phone: e.target.value })} />
                  {errors.phone && <div className="text-danger">{errors.phone}</div>}
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>{t("role")}</Form.Label>
                  <Form.Control as="select" value={userData.role} onChange={(e) => setUserData({ ...userData, role: e.target.value })}>
                    <option value="user">{t("user")}</option>
                    <option value="admin">{t("admin")}</option>
                    <option value="new">{t("new")}</option>
                  </Form.Control>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>{t("index")}</Form.Label>
                  <Form.Control type="text" value={userData.index} onChange={(e) => setUserData({ ...userData, index: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>{t("street")}</Form.Label>
                  <Form.Control type="text" value={userData.street} onChange={(e) => setUserData({ ...userData, street: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>{t("houseNumber")}</Form.Label>
                  <Form.Control type="text" value={userData.house_number} onChange={(e) => setUserData({ ...userData, house_number: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>{t("city")}</Form.Label>
                  <Form.Control type="text" value={userData.city} onChange={(e) => setUserData({ ...userData, city: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>{t("pesel")}</Form.Label>
                  <Form.Control type="text" value={userData.pesel} onChange={(e) => setUserData({ ...userData, pesel: e.target.value })} />
                  {errors.pesel && <div className="text-danger">{errors.pesel}</div>}
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>{t("accountTransfers")}</Form.Label>
                  <Form.Control type="text" value={userData.account_number} onChange={(e) => setUserData({ ...userData, account_number: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>{t("accountSalary")}</Form.Label>
                  <Form.Control type="text" value={userData.account_salary} onChange={(e) => setUserData({ ...userData, account_salary: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>{t("nameTransfers")}</Form.Label>
                  <Form.Control type="text" value={userData.name_transfers} onChange={(e) => setUserData({ ...userData, name_transfers: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>{t("nameSalary")}</Form.Label>
                  <Form.Control type="text" value={userData.name_salary} onChange={(e) => setUserData({ ...userData, name_salary: e.target.value })} />
                </Form.Group>
              </Form>
            </div>
            <div className="col-12 col-md-6">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="mb-0">{t("driverIdentifiers")}</h6>
                <AiOutlinePlus
                  size={22}
                  className="text-primary"
                  style={{ cursor: "pointer" }}
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowAddDriverIdentifier((v) => !v)}
                  onKeyDown={(e) => e.key === "Enter" && setShowAddDriverIdentifier((v) => !v)}
                  title={t("add")}
                />
              </div>
              {driverIdentifiers.length > 0 ? (
                <div className="d-flex flex-column gap-2 mb-3">
                  {driverIdentifiers.map((item) =>
                    editingIdentifierId === item.id ? (
                      <div key={item.id} className="rounded-3 p-3 border bg-light">
                        <Form.Group className="mb-2">
                          <Form.Label className="small">{t("driverCardName")}</Form.Label>
                          <Form.Control
                            size="sm"
                            value={editingIdentifierForm.name}
                            onChange={(e) => setEditingIdentifierForm((f) => ({ ...f, name: e.target.value }))}
                          />
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <Form.Label className="small">{t("driverCardNumber")}</Form.Label>
                          <Form.Control
                            size="sm"
                            value={editingIdentifierForm.cardNumber}
                            onChange={(e) => setEditingIdentifierForm((f) => ({ ...f, cardNumber: e.target.value }))}
                          />
                          {editingIdentifierError && (
                            <Form.Text className="text-danger d-block">{editingIdentifierError}</Form.Text>
                          )}
                        </Form.Group>
                        <div className="d-flex gap-1">
                          <Button variant="primary" size="sm" onClick={handleSaveEditDriverIdentifier}>
                            {t("save")}
                          </Button>
                          <Button variant="outline-secondary" size="sm" onClick={handleCancelEditDriverIdentifier}>
                            {t("cancel")}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div
                        key={item.id}
                        className="rounded-3 p-3 text-white position-relative overflow-hidden"
                        style={{
                          minHeight: 56,
                          background: "linear-gradient(135deg, #2d3748 0%, #1a202c 100%)",
                          boxShadow: "0 4px 14px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.08)",
                          border: "1px solid rgba(255,255,255,0.06)",
                        }}
                      >
                        <div className="position-absolute top-0 end-0 d-flex">
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1 text-white opacity-75"
                            style={{ minWidth: 32, fontSize: "1.1rem", textDecoration: "none" }}
                            onClick={() => handleEditDriverIdentifier(item)}
                            title={t("edit")}
                            aria-label={t("edit")}
                          >
                            <AiOutlineEdit />
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1 text-white opacity-75"
                            style={{ minWidth: 32, fontSize: "1.1rem", textDecoration: "none" }}
                            onClick={() => handleRemoveDriverIdentifier(item.id)}
                            title={t("delete")}
                            aria-label={t("delete")}
                          >
                            <AiOutlineDelete />
                          </Button>
                        </div>
                        <div className="d-flex align-items-center gap-2 small text-uppercase opacity-75 mb-1" style={{ letterSpacing: "0.08em", fontSize: "0.7rem" }}>
                          <AiOutlineCreditCard size={16} />
                          {item.name}
                        </div>
                        <div className="fw-bold text-end pe-5" style={{ letterSpacing: "0.2em", fontFamily: "ui-monospace, monospace", fontSize: "0.95rem" }}>{item.cardNumber}</div>
                      </div>
                    )
                  )}
                </div>
              ) : !showAddDriverIdentifier ? (
                <p className="text-muted small mb-0">{t("noDriverIdentifiersYet")}</p>
              ) : null}
              {showAddDriverIdentifier && (
                <Form>
                  <Form.Group className="mb-2">
                    <Form.Label>{t("driverCardName")}</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t("driverCardName")}
                      value={driverCardForm.name}
                      onChange={(e) => setDriverCardForm({ ...driverCardForm, name: e.target.value })}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>{t("driverCardNumber")}</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t("driverCardNumber")}
                      value={driverCardForm.cardNumber}
                      onChange={(e) => { setDriverCardForm({ ...driverCardForm, cardNumber: e.target.value }); setDriverCardFormError(""); }}
                    />
                    {driverCardFormError && (
                      <Form.Text className="text-danger d-block">{driverCardFormError}</Form.Text>
                    )}
                  </Form.Group>
                  <Button variant="outline-primary" size="sm" type="button" onClick={handleAddDriverIdentifier}>
                    {t("add")}
                  </Button>
                </Form>
              )}
              {/* Стандартні значення водія з API extra-funds-default-values */}
              <div className="mt-3 pt-3 border-top">
                <h6 className="mb-2 small fw-semibold">{t("driverDefaultValues")}</h6>
                {userData.role === "admin" ? (
                  <div
                    className="rounded-3 p-3 d-flex align-items-start gap-3 border"
                    style={{ backgroundColor: "rgba(108, 117, 125, 0.08)", borderColor: "rgba(108, 117, 125, 0.3)" }}
                  >
                    <span className="flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center text-secondary" style={{ width: 40, height: 40, backgroundColor: "rgba(108, 117, 125, 0.2)" }}>
                      <AiOutlineLock size={22} />
                    </span>
                    <div className="small text-secondary mb-0">{t("driverDefaultValuesAdmin")}</div>
                  </div>
                ) : userData.role === "new" ? (
                  <div
                    className="rounded-3 p-3 d-flex align-items-start gap-3 border"
                    style={{ backgroundColor: "rgba(255, 193, 7, 0.08)", borderColor: "rgba(255, 193, 7, 0.4)" }}
                  >
                    <span className="flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, backgroundColor: "rgba(255, 193, 7, 0.25)", color: "#b8860b" }}>
                      <AiOutlineClockCircle size={22} />
                    </span>
                    <div className="small text-dark mb-0" style={{ opacity: 0.9 }}>{t("driverDefaultValuesNew")}</div>
                  </div>
                ) : driverDefaultValuesLoading ? (
                  <div className="rounded-3 p-3 border bg-light text-muted small">
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    {t("loadingDefaultValues")}
                  </div>
                ) : driverDefaultValues == null ? (
                  <div
                    className="rounded-3 p-3 d-flex flex-column gap-2 border"
                    style={{ backgroundColor: "rgba(13, 110, 253, 0.06)", borderColor: "rgba(13, 110, 253, 0.25)" }}
                  >
                    <div className="d-flex align-items-start gap-3">
                      <span className="flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center text-primary" style={{ width: 40, height: 40, backgroundColor: "rgba(13, 110, 253, 0.15)" }}>
                        <AiOutlineInfoCircle size={22} />
                      </span>
                      <div className="small mb-0">{t("noDriverDefaultValues")}</div>
                    </div>
                    <Button as={Link} variant="outline-primary" size="sm" className="align-self-start" to="/admin/extra-funds-default-values">
                      <AiOutlineSetting className="me-1" size={16} />
                      {t("goToStandardValuesPage")}
                    </Button>
                  </div>
                ) : (
                  <div className="rounded-3 p-3 border" style={{ backgroundColor: "rgba(25, 135, 84, 0.06)", borderColor: "rgba(25, 135, 84, 0.3)" }}>
                    <div className="d-flex align-items-center gap-2 small mb-2 text-success">
                      <AiOutlineCheckCircle size={18} />
                      <span>{t("driverDefaultValuesSet")}</span>
                    </div>
                    <div className="d-flex flex-column gap-2 mb-2">
                      {[
                        { key: "taxesZus", labelKey: "zus", Icon: AiOutlineBank },
                        { key: "partner", labelKey: "partner", Icon: AiOutlineTeam },
                        { key: "accountant", labelKey: "accountant", Icon: AiOutlineFileText },
                        { key: "carRent", labelKey: "carRent", Icon: AiOutlineCar },
                        { key: "sallary", labelKey: "sallary", Icon: AiOutlineBank },
                      ].map(({ key, labelKey, Icon }) => (
                        <div key={key} className="bg-white rounded px-2 py-1 border shadow-sm d-flex align-items-center gap-2 w-100" style={{ fontSize: "0.8rem" }}>
                          <Icon size={14} className="text-secondary flex-shrink-0" />
                          <span className="flex-grow-1">{t(labelKey)}</span>
                          <strong>{driverDefaultValues[key] != null ? String(driverDefaultValues[key]) : "0"}</strong>
                        </div>
                      ))}
                    </div>
                    {(() => {
                      const otherExpenses = Array.isArray(driverDefaultValues.otherExpenses) ? driverDefaultValues.otherExpenses : [];
                      const refundsItems = Array.isArray(driverDefaultValues.refundsItems) ? driverDefaultValues.refundsItems : [];
                      if (otherExpenses.length === 0 && refundsItems.length === 0) return null;
                      return (
                        <div className="mt-2 pt-2 border-top d-flex flex-column gap-2">
                          {otherExpenses.length > 0 && (
                            <div className="small">
                              <div className="text-danger fw-semibold mb-1">{t("otherCosts")}</div>
                              {otherExpenses.map((e, i) => (
                                <Card key={`oe-${i}`} className="border-0 shadow-sm mb-1" style={{ borderLeft: "3px solid var(--bs-danger)", backgroundColor: "rgba(220, 53, 69, 0.08)" }}>
                                  <Card.Body className="py-1 px-2 d-flex align-items-center gap-2">
                                    <AiOutlineMinus size={14} className="text-danger flex-shrink-0" />
                                    <span className="text-break">{e.amount != null && e.amount !== "" ? String(e.amount) : "—"}{e.description ? ` — ${e.description}` : ""}</span>
                                  </Card.Body>
                                </Card>
                              ))}
                            </div>
                          )}
                          {refundsItems.length > 0 && (
                            <div className="small">
                              <div className="text-success fw-semibold mb-1">{t("refunds")}</div>
                              {refundsItems.map((r, i) => (
                                <Card key={`rf-${i}`} className="border-0 shadow-sm mb-1" style={{ borderLeft: "3px solid var(--bs-success)", backgroundColor: "rgba(25, 135, 84, 0.08)" }}>
                                  <Card.Body className="py-1 px-2 d-flex align-items-center gap-2">
                                    <AiOutlinePlus size={14} className="text-success flex-shrink-0" />
                                    <span className="text-break">{r.amount != null && r.amount !== "" ? String(r.amount) : "—"}{r.description ? ` — ${r.description}` : ""}</span>
                                  </Card.Body>
                                </Card>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
              {/* Авто водія з API fleet */}
              <div className="mt-3 pt-3 border-top">
                <h6 className="mb-2 small fw-semibold">{t("driverVehicle")}</h6>
                {driverVehicleLoading ? (
                  <div className="small text-muted">
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    {t("loadingDefaultValues")}
                  </div>
                ) : !driverVehicle ? (
                  <p className="small text-muted mb-0">{t("noDriverVehicle")}</p>
                ) : (
                  <Card className="border shadow-sm overflow-hidden" style={{ borderRadius: "12px", borderColor: "rgba(0,0,0,0.08)" }}>
                    <Card.Body className="p-0">
                      <div className="p-2 px-3" style={{ backgroundColor: "rgba(30, 58, 95, 0.06)" }}>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0 text-primary" style={{ width: 40, height: 40, backgroundColor: "rgba(13, 110, 253, 0.12)" }}>
                            <AiOutlineCar size={22} />
                          </span>
                          <span className="fw-bold text-dark flex-shrink-0" style={{ fontSize: "1rem", letterSpacing: "0.08em", fontFamily: "ui-monospace, monospace" }}>{driverVehicle.plate || "—"}</span>
                          <span className="text-muted flex-shrink-0" style={{ fontSize: "0.85rem" }}>·</span>
                          <span className="text-dark flex-grow-1 min-w-0" style={{ fontSize: "0.9rem" }}>{driverVehicle.brand || "—"} {driverVehicle.model || ""}</span>
                          {driverVehicle.year != null && driverVehicle.year !== "" && (
                            <>
                              <span className="text-muted flex-shrink-0" style={{ fontSize: "0.85rem" }}>·</span>
                              <span className="text-dark flex-shrink-0" style={{ fontSize: "0.9rem" }}>{driverVehicle.year}</span>
                            </>
                          )}
                          {driverVehicle.status === "under_payment" && (
                            <span className="badge rounded-pill px-2 py-1 flex-shrink-0" style={{ backgroundColor: "rgba(255, 193, 7, 0.25)", color: "#856404", fontSize: "0.75rem" }}>
                              {t("fleet.statusUnderPayment")}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="px-3 py-2 border-top bg-white">
                        <Button as={Link} variant="primary" size="sm" className="w-100 d-inline-flex align-items-center justify-content-center gap-2" to={`/admin/fleet/${driverVehicle.id}`}>
                          {t("viewVehiclePage")}
                          <span className="opacity-75" aria-hidden>→</span>
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleUpdate}>
            {t("save")}
          </Button>
          <button className="btn btn-warning" onClick={() => handleResetPassword(selectedUser.id)}>
            {t("resetPassword")}
          </button>

          <Button variant="secondary" onClick={() => setEditModal(false)}>
            {t("close")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Підтвердження надання ролі адміністратора — Material-like на Bootstrap */}
      <Modal
        show={!!pendingAdminRole}
        onHide={() => setPendingAdminRole(null)}
        centered
        className="modal-confirm-admin"
        dialogClassName="rounded-3 overflow-hidden border-0"
        contentClassName="border-0 bg-white"
      >
        <Modal.Header
          className="border-0 py-3 px-4 text-white"
          style={{ backgroundColor: "#d32f2f" }}
          closeButton
          closeVariant="white"
        >
          <Modal.Title className="d-flex align-items-center gap-2 fw-semibold" style={{ fontSize: "1.25rem" }}>
            <span className="rounded-circle d-flex align-items-center justify-content-center bg-white bg-opacity-25" style={{ width: 36, height: 36 }}>
              <AiOutlineSafetyCertificate size={22} className="text-white" />
            </span>
            {t("confirmAdminRoleTitle")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 py-4 border-0 bg-white" style={{ border: "none" }}>
          <p className="mb-0 text-dark" style={{ fontSize: "0.9375rem", lineHeight: 1.6 }}>
            {pendingAdminRole && (
              <Trans
                i18nKey="confirmAdminRoleMessage"
                values={{ name: `${pendingAdminRole.user.firstName} ${pendingAdminRole.user.lastName}`, email: pendingAdminRole.user.email }}
                components={[<strong />, <strong />]}
              />
            )}
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 gap-2 px-4 pb-4 pt-0 bg-white" style={{ border: "none" }}>
          <Button
            variant="outline-secondary"
            className="rounded-2 px-3"
            onClick={() => setPendingAdminRole(null)}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="danger"
            className="rounded-2 px-3 text-white"
            style={{ backgroundColor: "#d32f2f", borderColor: "#d32f2f" }}
            onClick={handleConfirmAdminRole}
          >
            {t("confirm")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UsersTab;
